import React, { useState, useEffect } from "react";
import {
    Box,
    Typography,
    Button,
    Grid,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    TablePagination,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getAuthData } from "../../utils/auth";

const apiUrl = process.env.REACT_APP_API_URL;

const SubirInventario = () => {
    const [columbarioLado1Img, setColumbarioLado1Img] = useState(null);
    const [columbarioLado2Img, setColumbarioLado2Img] = useState(null);
    const [columbarioLado3Img, setColumbarioLado3Img] = useState(null);
    const [columbarioLado4Img, setColumbarioLado4Img] = useState(null);
    const [capillaSeleccionada, setCapillaSeleccionada] = useState("");
    const [loadingImages, setLoadingImages] = useState(false);
    const [loadingCsv, setLoadingCsv] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [columbarios, setColumbarios] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalColumbarios, setTotalColumbarios] = useState(0);

    const authData = getAuthData();
    const token = authData?.token;

    useEffect(() => {
        fetchColumbarios();
    }, [page, rowsPerPage]);

    const fetchColumbarios = async () => {
        try {

            const config = {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    skip: page * rowsPerPage,
                    limit: rowsPerPage
                }
            };
            const response = await axios.get(`${apiUrl}/api/columbarios`, config);
            if (response.data.items) {
                setColumbarios(response.data.items);
                setTotalColumbarios(response.data.total);
            } else {
                setColumbarios(response.data);
                setTotalColumbarios(response.data.length);
            }
        } catch (error) {
            console.error("Error al obtener los columbarios:", error);
            toast.error("Error al cargar la lista de columbarios.");
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFileChange = (event, setImage) => {
        const file = event.target.files[0];
        if (file && file.type.startsWith("image/")) {
            setImage(file);
        } else {
            toast.error("Por favor, selecciona un archivo de imagen válido.");
        }
    };

    const handleCsvFileChange = (event) => {
        const file = event.target.files[0];
        if (file && (file.type === "text/csv" || file.name.endsWith(".csv") || file.name.endsWith(".xls") || file.name.endsWith(".xlsx"))) {
            setCsvFile(file);
        } else {
            toast.error("Por favor, selecciona un archivo CSV válido.");
        }
    };

    const handleCsvSubmit = async () => {
        if (!csvFile) {
            toast.error("Debes seleccionar un archivo CSV para subir.");
            return;
        }

        setLoadingCsv(true);
        const formData = new FormData();
        formData.append("file", csvFile);

        try {

            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            };

            await axios.post(`${apiUrl}/api/admin/upload-columbarios`, formData, config);
            toast.success("Columbarios actualizados exitosamente.");
            setCsvFile(null);
            fetchColumbarios(); // Recargar la tabla
        } catch (error) {
            console.error("Error al subir archivo CSV:", error.response?.data || error.message);
            toast.error("Error al actualizar columbarios.");
        } finally {
            setLoadingCsv(false);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (
            !columbarioLado1Img &&
            !columbarioLado2Img &&
            !columbarioLado3Img &&
            !columbarioLado4Img
        ) {
            toast.error("Debes subir al menos una imagen antes de enviar.");
            return;
        }

        setLoadingImages(true);
        const formData = new FormData();
        formData.append("capilla", capillaSeleccionada);
        if (columbarioLado1Img)
            formData.append("Lado_A", columbarioLado1Img);
        if (columbarioLado2Img)
            formData.append("Lado_B", columbarioLado2Img);
        if (columbarioLado3Img)
            formData.append("Lado_C", columbarioLado3Img);
        if (columbarioLado4Img)
            formData.append("Lado_D", columbarioLado4Img);

        try {
            const token = localStorage.getItem("access_token");
            const config = {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                },
            };

            await axios.post(`${apiUrl}/api/upload/inventario`, formData, config);
            toast.success("Imágenes actualizadas exitosamente.");
            setColumbarioLado1Img(null);
            setColumbarioLado2Img(null);
            setColumbarioLado3Img(null);
            setColumbarioLado4Img(null);
        } catch (error) {
            console.error(
                "Error al subir las imágenes:",
                error.response?.data || error.message
            );
            toast.error("Error al actualizar las imágenes.");
        } finally {
            setLoadingImages(false);
        }
    };

    return (
        <Box
            elevation={3}
            sx={{
                margin: "0 auto",
                padding: 3,
                boxShadow: 3,
                backgroundColor: "#ffffff",
                borderRadius: 2,
            }}
        >
            <Typography
                sx={{
                    fontFamily: "Bakbak One, sans-serif",
                    color: "#0A4575",
                    mb: 2,
                }}
                variant="h6"
                gutterBottom
            >
                SUBIR MAPAS DE COLUMBARIOS
            </Typography>

            <form onSubmit={handleSubmit}>
                <Grid container spacing={4}>

                    {/* COLUMNA IZQUIERDA — SELECT */}
                    <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small"> {/* Agregamos size="small" aquí */}
                            <InputLabel id="capilla-label">Capilla</InputLabel>
                            <Select
                                labelId="capilla-label" // Vincula el label con el select
                                value={capillaSeleccionada}
                                label="Capilla" // Esto es lo que crea el hueco en el borde
                                onChange={(e) => setCapillaSeleccionada(e.target.value)}
                                sx={{ boxShadow: 3 }}
                            >
                                <MenuItem value="CP1">Capilla Principal</MenuItem>
                                <MenuItem value="CC1">Capilla de Contemplación 01</MenuItem>
                                <MenuItem value="CC2">Capilla de Contemplación 02</MenuItem>
                                <MenuItem value="CC3">Capilla de Contemplación 03</MenuItem>
                                <MenuItem value="CC4">Capilla de Contemplación 04</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>

                    {/* COLUMNA DERECHA — CONTENIDO */}
                    <Grid item xs={12} md={9}>
                        {capillaSeleccionada && (
                            <>
                                <Typography
                                    variant="h6"
                                    sx={{ mb: 3, fontWeight: "bold", color: "#0A4575" }}
                                >
                                    Mapas de Capilla {capillaSeleccionada}
                                </Typography>

                                {/* FILAS POR LADO */}
                                {['A', 'B', 'C', 'D'].map((lado, index) => {
                                    const setImg = [
                                        setColumbarioLado1Img,
                                        setColumbarioLado2Img,
                                        setColumbarioLado3Img,
                                        setColumbarioLado4Img,
                                    ][index];

                                    const img = [
                                        columbarioLado1Img,
                                        columbarioLado2Img,
                                        columbarioLado3Img,
                                        columbarioLado4Img,
                                    ][index];

                                    return (
                                        <Grid
                                            container
                                            spacing={2}
                                            alignItems="center"
                                            key={lado}
                                            sx={{ mb: 2 }}
                                        >
                                            <Grid item xs={12} md={3}>
                                                <Button
                                                    variant="contained"
                                                    component="label"
                                                    color="primary"
                                                    fullWidth
                                                >
                                                    Subir Lado {lado}
                                                    <input
                                                        type="file"
                                                        hidden
                                                        accept="image/*"
                                                        onChange={(e) => handleFileChange(e, setImg)}
                                                    />
                                                </Button>
                                            </Grid>

                                            <Grid item xs={12} md={8}>
                                                <Typography variant="subtitle2" sx={{ color: "#0A4575" }}>
                                                    <b>Lado {lado}:</b> {img ? img.name : "Sin archivo seleccionado"}
                                                </Typography>
                                            </Grid>
                                        </Grid>
                                    );
                                })}

                                {/* BOTÓN FINAL */}
                                <Box display="flex" justifyContent="flex-end" mt={2}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        disabled={loadingImages}
                                        sx={{
                                            minWidth: 200,
                                            fontWeight: "bold",
                                            boxShadow: 3
                                        }}
                                    >
                                        {loadingImages ? <CircularProgress size={24} /> : "SUBIR IMÁGENES"}
                                    </Button>
                                </Box>
                            </>
                        )}
                    </Grid>
                </Grid>
            </form>

            {/* SECCIÓN SUBIR INVENTARIO CSV */}
            <Typography
                sx={{
                    fontFamily: "Bakbak One, sans-serif",
                    color: "#0A4575",
                    mt: 2,
                    mb: 2,
                }}
                variant="h6"
            >
                ACTUALIZAR INVENTARIO DESDE ARCHIVO EXCEL
            </Typography>

            <Grid
                container
                spacing={2}
                alignItems="center"
                sx={{ mb: 4 }}
            >
                {/* BOTÓN SELECCIONAR */}
                <Grid item xs={12} sm="auto">
                    <Button
                        variant="outlined"
                        component="label"
                    >
                        SELECCIONAR ARCHIVO DE INVENTARIO
                        <input
                            type="file"
                            hidden
                            accept=".csv,.xlsx,.xls"
                            onChange={handleCsvFileChange}
                        />
                    </Button>
                </Grid>

                {/* NOMBRE ARCHIVO */}
                <Grid item xs={12} sm="auto">
                    {csvFile && (
                        <Typography sx={{ fontWeight: "bold" }}>
                            {csvFile.name}
                        </Typography>
                    )}
                </Grid>

                {/* BOTÓN SUBIR */}
                <Grid item xs={12} sm="auto">
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={loadingCsv || !csvFile}
                        onClick={handleCsvSubmit}
                        sx={{ minWidth: 150 }}
                    >
                        {loadingCsv ? <CircularProgress size={24} /> : "Subir Archivo"}
                    </Button>
                </Grid>
            </Grid>

            <Typography
                sx={{
                    fontFamily: "Bakbak One, sans-serif",
                    color: "#0A4575",
                    mb: 2,
                }}
                variant="h6"
            >
                LISTA DE COLUMBARIOS
            </Typography>
            <TableContainer component={Paper} elevation={3}>
                <Table size="small">
                    <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>codigo</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>tipo</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>capilla</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>lado</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>cantidad_nichos</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>fila</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>columna</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>ubicacion</TableCell>
                            <TableCell sx={{ fontWeight: "bold", border: "1px solid #ddd" }}>estado</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {columbarios.map((row) => (
                            <TableRow key={row.id || row.codigo} hover>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.codigo}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.tipo}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.capilla}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.lado}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.cantidad_nichos}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.fila}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.columna}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.ubicacion}</TableCell>
                                <TableCell sx={{ border: "1px solid #ddd" }}>{row.estado}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                component="div"
                count={totalColumbarios}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                labelRowsPerPage="Filas por página"
            />
        </Box>
    );
};

export default SubirInventario;
